import {
  formatPrice,
  formatVolume,
  getBinaryOdds,
  getH2HLabels,
  getOutcomeDisplayPrice,
  groupOutcomesBySelection,
  MARKET_TYPES,
  STATUS_META,
} from "./bondPredictHelpers";
import { formatAppDateTime } from "./appTimeFormat";

export const formatTradeRow = (trade) => {
  const side = (trade?.takerSide || trade?.side || "").toString().toUpperCase();
  const outcomeName =
    (typeof trade?.outcome === "object" && trade.outcome?.label) ||
    trade?.outcomeName ||
    "—";
  const gross = trade?.grossAmount;
  const derived = Number(trade?.price) * Number(trade?.quantity);
  const amount =
    typeof gross === "number" && Number.isFinite(gross) ? gross : derived;
  const quantity = Number(trade?.quantity);
  const p = Number(trade?.price);
  return {
    side,
    outcomeName,
    amount: Number.isFinite(amount) ? amount.toFixed(2) : "—",
    quantity: Number.isFinite(quantity) ? quantity.toFixed(2) : null,
    price: Number.isFinite(p) ? p : null,
    createdAt: trade?.createdAt,
  };
};

export const formatTradeTime = (value) => {
  if (!value) return "—";
  try {
    return formatAppDateTime(value);
  } catch {
    return "—";
  }
};

export const formatLineDate = (value) => {
  if (!value) return "—";
  try {
    return formatAppDateTime(value);
  } catch {
    return "—";
  }
};

export const formatPrice01 = (price) => {
  const n = Number(price);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(4);
};

export const formatCoinTotal = (price01, shares) => {
  const n = Number(price01) * Number(shares);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(2);
};

export const buildContractLineMap = (market) => {
  const map = {};
  (market?.contractLines || []).forEach((line) => {
    if (!line) return;
    const key = String(line.selectionKey || "").trim();
    if (!key) return;
    map[key] = line;
  });
  return map;
};

export const buildLineResolutionMap = (market) => {
  const map = {};
  (market?.multiBinaryResolutions || []).forEach((r) => {
    if (!r) return;
    const key = String(r.selectionKey || "").trim();
    if (!key) return;
    map[key] = r;
  });
  return map;
};

export const effectiveLineMeta = (market, line, fallbackStatus) => {
  const status = line?.status || fallbackStatus || market?.status || "OPEN";
  const closeAt = line?.timings?.closeAt || market?.timings?.closeAt || null;
  const resolveBy = line?.timings?.resolveBy || market?.timings?.resolveBy || null;
  const openAt = line?.timings?.openAt || market?.timings?.openAt || null;
  return { status, closeAt, resolveBy, openAt };
};

export const isLineTradableNow = (line, market, fallbackStatus) => {
  const { status, closeAt } = effectiveLineMeta(market, line, fallbackStatus);
  if (status !== "OPEN") return false;
  if (!closeAt) return true;
  return new Date(closeAt).getTime() > Date.now();
};

export const isYesWinnerForLine = (group, lineResolution) => {
  if (!group?.yes || !lineResolution?.winningOutcome) return false;
  const winId =
    typeof lineResolution.winningOutcome === "object"
      ? lineResolution.winningOutcome?._id
      : lineResolution.winningOutcome;
  return String(winId) === String(group.yes._id || group.yes.id);
};

export const sumLineOutcomeMetric = (yesOutcome, noOutcome, key) => {
  const a = yesOutcome?.metrics?.[key];
  const b = noOutcome?.metrics?.[key];
  if (a == null && b == null) return null;
  return (Number(a) || 0) + (Number(b) || 0);
};

export const nonTradableLineActionLabel = (lineStatus, isVoid) => {
  if (lineStatus === "ON_HOLD") return "Paused";
  if (isVoid) return lineStatus === "REFUNDED" ? "Refunded" : "Voided";
  const meta = STATUS_META[lineStatus];
  if (meta?.label) return meta.label;
  return "Closed";
};

export const normalizeMatchupText = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\s*[·•]\s*/g, " ")
    .replace(/\b(vs\.?|versus|v\.)\b/g, "vs");

export const h2hTitleDuplicatesMatchup = (title, homeName, awayName) => {
  const h = (homeName || "").trim();
  const a = (awayName || "").trim();
  if (!h && !a) return false;
  const matchup = normalizeMatchupText(`${h} vs ${a}`);
  const t = normalizeMatchupText(title || "");
  if (!t) return true;
  if (t === matchup) return true;
  const beforeExtras = t.split(/\s*[·•|—–-]\s*/)[0]?.trim() || t;
  return normalizeMatchupText(beforeExtras) === matchup;
};

export const getMultiLineCounts = (market) => {
  const grouped = groupOutcomesBySelection(market?.outcomes || []).filter(
    (g) => g.yes || g.no
  );
  const lineMap = buildContractLineMap(market);
  const status = market?.status || "OPEN";
  const live = grouped.filter((g) =>
    isLineTradableNow(lineMap[g.selectionKey], market, status)
  ).length;
  return { total: grouped.length, live };
};

export const getDefaultSelectedOutcome = (market) => {
  const outcomes = Array.isArray(market?.outcomes)
    ? market.outcomes.filter((o) => o && o._id)
    : [];
  if (!outcomes.length) return null;

  const type = market?.type || MARKET_TYPES.BINARY;
  if (type === MARKET_TYPES.BINARY) {
    return outcomes.find((o) => o.side === "YES" || o.label === "Yes") || outcomes[0];
  }

  const grouped = groupOutcomesBySelection(outcomes);
  if (type === MARKET_TYPES.HEAD_TO_HEAD) {
    const g = grouped.find((x) => x.yes);
    return g?.yes || outcomes[0];
  }

  const multi = grouped.filter((g) => g.yes);
  return multi[0]?.yes || outcomes[0];
};

export const aggregateOrderLevels = (levels = [], sort = "desc") => {
  const map = {};
  (Array.isArray(levels) ? levels : []).forEach((lvl) => {
    if (!lvl) return;
    const price = lvl.price;
    const qty = lvl.quantity;
    if (typeof price !== "number" || Number.isNaN(price)) return;
    if (typeof qty !== "number" || Number.isNaN(qty)) return;
    const key = price.toFixed(4);
    if (!map[key]) map[key] = { price, quantity: 0 };
    map[key].quantity += qty;
  });
  const arr = Object.values(map);
  arr.sort((a, b) => (sort === "asc" ? a.price - b.price : b.price - a.price));
  return arr;
};

export const getOrderBookSummary = (orderBook) => {
  const bids = aggregateOrderLevels(orderBook?.bids, "desc");
  const asks = aggregateOrderLevels(orderBook?.asks, "asc");
  const bestBid = bids[0]?.price ?? orderBook?.bestBid ?? null;
  const bestAsk = asks[0]?.price ?? orderBook?.bestAsk ?? null;
  const spread = bestBid != null && bestAsk != null ? bestAsk - bestBid : null;
  return { bids, asks, bestBid, bestAsk, spread };
};

export const outcomeRefToLabel = (market, outcomeRef) => {
  if (outcomeRef == null) return "";
  if (typeof outcomeRef === "object" && outcomeRef !== null) {
    const direct = [outcomeRef.label, outcomeRef.side, outcomeRef.name]
      .map((x) => (x != null && String(x).trim() !== "" ? String(x).trim() : ""))
      .find(Boolean);
    if (direct) return direct;
    if (outcomeRef._id) return outcomeRefToLabel(market, outcomeRef._id);
    return "";
  }
  const id = String(outcomeRef).trim();
  if (!id) return "";
  const found = (market?.outcomes || []).find((o) => o && String(o._id) === id);
  if (found) {
    const t = String(found.label || found.side || "").trim();
    if (t) return t;
  }
  return "";
};

export const getResolutionRows = (market) => {
  const res = market?.resolution;
  if (!res) return [];
  if (typeof res === "string") return [{ label: "Resolution", value: res }];
  if (typeof res === "object") {
    const winLabel = outcomeRefToLabel(market, res.outcome);
    return [
      { label: "Status", value: res.status },
      ...(winLabel ? [{ label: "Outcome", value: winLabel }] : []),
      { label: "Rationale", value: res.rationale },
    ].filter((r) => r.value != null && String(r.value).trim() !== "");
  }
  return [];
};

export const getMarketScheduleRows = (market) => {
  const t = market?.timings || {};
  return [
    { label: "Opens", value: t.openAt, format: formatLineDate },
    { label: "Closes", value: t.closeAt, format: formatLineDate },
    { label: "Result by", value: t.resolveBy, format: formatLineDate },
  ].filter((r) => r.value);
};

/**
 * How Step 1 should lay out selectable outcomes for this market type.
 * - BINARY / H2H: one row, two legs (Yes/No or home/away)
 * - MULTI_BINARY: one row per contract line, each with Yes + No legs
 */
export const getOutcomeLayoutMeta = (market) => {
  const type = market?.type || MARKET_TYPES.BINARY;
  const outcomes = Array.isArray(market?.outcomes)
    ? market.outcomes.filter((o) => o && o._id)
    : [];
  const grouped = groupOutcomesBySelection(outcomes).filter(
    (g) => g.yes || g.no || g.direct
  );
  const lineCount = grouped.length;
  const legCount = outcomes.length;

  if (type === MARKET_TYPES.BINARY) {
    return {
      type,
      mode: "duo",
      lineCount: 1,
      legCount: Math.max(legCount, 2),
      rootClass: "bp-detail-layout-duo bp-detail-layout-binary",
      summary: "2 outcomes · Yes and No",
    };
  }

  if (type === MARKET_TYPES.HEAD_TO_HEAD) {
    return {
      type,
      mode: "versus",
      lineCount: 1,
      legCount: Math.max(legCount, 2),
      rootClass: "bp-detail-layout-duo bp-detail-layout-h2h",
      summary: "2 sides · pick the team you back",
    };
  }

  const grid = lineCount >= 4;
  return {
    type,
    mode: grid ? "lines-grid" : "lines-stack",
    lineCount,
    legCount,
    rootClass: `bp-detail-layout-lines${grid ? " bp-detail-layout-lines-grid" : ""}`,
    summary: `${lineCount} line${lineCount === 1 ? "" : "s"} · ${legCount} outcomes`,
  };
};

export const getSelectedOutcomeLabel = (outcome, market) => {
  if (!outcome) return "—";
  const type = market?.type || MARKET_TYPES.BINARY;

  if (type === MARKET_TYPES.HEAD_TO_HEAD) {
    const h2h = getH2HLabels(market);
    if (h2h) {
      if (outcome.side === "YES") return h2h.labelA;
      if (outcome.side === "NO") return h2h.labelB;
    }
  }

  if (type === MARKET_TYPES.BINARY) {
    if (outcome.side === "YES" || /^yes$/i.test(outcome.label || "")) return "Yes";
    if (outcome.side === "NO" || /^no$/i.test(outcome.label || "")) return "No";
  }

  if (type === MARKET_TYPES.MULTI_BINARY) {
    const baseLabel = (outcome.label || outcome.selectionKey || "")
      .replace(/\s*\((Yes|No)\)\s*$/i, "")
      .trim();
    let sideLabel = "";
    if (outcome.side === "YES") sideLabel = "Yes";
    else if (outcome.side === "NO") sideLabel = "No";
    else {
      const match = (outcome.label || "").match(/\((Yes|No)\)\s*$/i);
      if (match) sideLabel = match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
    }
    if (baseLabel && sideLabel) return `${baseLabel} · ${sideLabel}`;
    return baseLabel || sideLabel || "Outcome";
  }

  const side = outcome.side ? String(outcome.side) : "";
  let label = outcome.label || outcome.selectionKey || "";
  if (label && side && !label.toLowerCase().includes(side.toLowerCase())) {
    return `${label} · ${side}`;
  }
  return label || side || "Outcome";
};

export const getSelectedOutcomePrice = (outcome) => formatPrice(getOutcomeDisplayPrice(outcome));

/** Raw 0–1 implied probability for the selected outcome (null when unknown). */
export const getOutcomeProbability = (outcome) => {
  const p = getOutcomeDisplayPrice(outcome);
  return typeof p === "number" && Number.isFinite(p) ? p : null;
};

/**
 * A single, headline reading of "where the crowd stands" for any market type.
 * - BINARY: Yes vs No split (sums to 100).
 * - HEAD_TO_HEAD: side A vs side B split (normalised to 100).
 * - MULTI_BINARY: the single most-likely contract line.
 * Returns null when there is nothing meaningful to show.
 */
export const getMarketConsensus = (market) => {
  const type = market?.type || MARKET_TYPES.BINARY;

  if (type === MARKET_TYPES.BINARY) {
    const { yesPct, noPct } = getBinaryOdds(market);
    if (yesPct == null && noPct == null) return null;
    const y = yesPct != null ? yesPct : Math.max(0, 100 - (noPct || 0));
    const n = noPct != null ? noPct : Math.max(0, 100 - (yesPct || 0));
    const segments = [
      { key: "yes", label: "Yes", pct: y, tone: "yes" },
      { key: "no", label: "No", pct: n, tone: "no" },
    ];
    const lead = y >= n ? segments[0] : segments[1];
    return { kind: "binary", segments, lead, caption: "Live market consensus" };
  }

  if (type === MARKET_TYPES.HEAD_TO_HEAD) {
    const h2h = getH2HLabels(market);
    if (!h2h) return null;
    const a = typeof h2h.priceA === "number" ? Math.max(0, h2h.priceA * 100) : null;
    const b = typeof h2h.priceB === "number" ? Math.max(0, h2h.priceB * 100) : null;
    if (a == null && b == null) return null;
    const total = (a || 0) + (b || 0) || 1;
    const pa = Math.round(((a || 0) / total) * 100);
    const segments = [
      { key: "a", label: h2h.labelA, pct: pa, tone: "yes" },
      { key: "b", label: h2h.labelB, pct: 100 - pa, tone: "no" },
    ];
    const lead = segments[0].pct >= segments[1].pct ? segments[0] : segments[1];
    return { kind: "h2h", segments, lead, caption: "Who the market favours" };
  }

  // MULTI_BINARY — surface the single most-likely line.
  const grouped = groupOutcomesBySelection(market?.outcomes || []).filter(
    (g) => g.yes || g.no
  );
  if (!grouped.length) return null;
  const ranked = grouped
    .map((g) => {
      const price = getOutcomeDisplayPrice(g.yes);
      const label = (g.yes?.label || g.no?.label || g.selectionKey || "").replace(
        /\s*\((Yes|No)\)\s*$/i,
        ""
      );
      return {
        key: g.selectionKey,
        label,
        pct: typeof price === "number" ? Math.round(price * 100) : null,
      };
    })
    .filter((r) => r.pct != null)
    .sort((a, b) => b.pct - a.pct);
  if (!ranked.length) return null;
  const lead = ranked[0];
  return {
    kind: "multi",
    segments: [{ key: lead.key, label: lead.label, pct: lead.pct, tone: "yes" }],
    lead: { label: lead.label, pct: lead.pct, tone: "yes" },
    others: ranked.slice(1, 4),
    total: grouped.length,
    caption: `Front-runner of ${grouped.length} lines`,
  };
};

export const isOutcomeTradable = (market, outcome) => {
  if (!market || !outcome) return false;
  if (outcome.isTradeable === false) return false;
  const type = market.type || MARKET_TYPES.BINARY;
  if (type !== MARKET_TYPES.MULTI_BINARY) {
    return (market.status || "OPEN") === "OPEN";
  }
  const lineMap = buildContractLineMap(market);
  const line = lineMap[outcome.selectionKey];
  return isLineTradableNow(line, market, market.status);
};

export const getTradeCtaLabel = (market, selectedOutcome, tradable) => {
  if (!selectedOutcome) return "Select an outcome";
  if (!tradable) {
    const type = market?.type || MARKET_TYPES.BINARY;
    if (type === MARKET_TYPES.MULTI_BINARY) {
      const lineMap = buildContractLineMap(market);
      const line = lineMap[selectedOutcome.selectionKey];
      const meta = effectiveLineMeta(market, line, market.status);
      const isVoid = meta.status === "VOID" || meta.status === "REFUNDED";
      return nonTradableLineActionLabel(meta.status, isVoid);
    }
    const status = market?.status || "OPEN";
    if (status === "RESOLVED" || status === "FINALIZED") {
      return STATUS_META[status]?.label || status;
    }
    if (status === "VOID" || status === "REFUNDED") return "Voided";
    if (status === "ON_HOLD") return "Paused";
    return "Closed";
  }
  const lbl = getSelectedOutcomeLabel(selectedOutcome, market);
  return lbl !== "—" ? `Trade · ${lbl}` : "Place order";
};
